import type { ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

export interface ToggleProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange'> {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
  label?: string;
  description?: string;
}

/**
 * Toggle / Switch. Two-state on/off control.
 * Wrapped in a button for proper a11y (role=switch).
 */
export function Toggle({
  checked,
  onCheckedChange,
  label,
  description,
  className,
  disabled,
  ...rest
}: ToggleProps) {
  return (
    <label
      className={cn(
        'flex cursor-pointer items-center justify-between gap-4 border-b border-dashed border-border-subtle py-3.5 last:border-b-0',
        disabled && 'cursor-not-allowed opacity-50',
        className
      )}
    >
      <div className="min-w-0 flex-1">
        {label && <div className="text-sm font-medium text-text-primary">{label}</div>}
        {description && (
          <div className="mt-0.5 max-w-md text-xs text-text-secondary">{description}</div>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => !disabled && onCheckedChange(!checked)}
        className={cn(
          'relative h-5 w-9 flex-shrink-0 rounded-pill',
          'transition-colors duration-fast',
          checked ? 'bg-brand-500' : 'bg-neutral-300',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2'
        )}
        {...rest}
      >
        <span
          className={cn(
            'absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm',
            'transition-transform duration-fast ease-out',
            checked ? 'translate-x-[18px]' : 'translate-x-0.5'
          )}
        />
      </button>
    </label>
  );
}
